import { HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable, OnDestroy, inject } from '@angular/core';

import { Store } from '@ngrx/store';
import { NEVER, Observable, PartialObserver, Subscription, catchError, share, take, takeUntil, tap } from 'rxjs';

import { noop, unsubscribe } from '@fcom/core/utils';
import { DapiErrorCode, DapiHttpErrorResponse } from '@fcom/dapi';
import { SeatMapStatus } from '@fcom/common/interfaces/seat-map.interface';
import {
  Category,
  CheckInReassignExitSeatsRequest,
  FinnairAdvancePassengerInformationRequest,
  FinnairAdvancePassengerInformationRequestV2,
  FinnairEmergencyContactUpdateRequest,
  FinnairItineraryItemFlight,
  FinnairOrder,
  FinnairPassengerItem,
  FinnairPassengerRequest,
  FinnairServiceRequestItem,
  FinnairTravelEndpoint,
  RequestInitiator,
  SeatmapSvgAndControlData,
} from '@fcom/dapi/api/models';
import { AppState, mapErrorForSentry, SentryLogger, TzDate } from '@fcom/core';
import { MmbOrderService } from '@fcom/manage-booking-common';
import { DataCloudService } from '@fcom/common/datacloud';
import { GtmService } from '@fcom/common/gtm';
import {
  ElementActions,
  ElementTypes,
  GaContext,
  getCarTrawlerDateTime,
  getCarTrawlerLang,
  buildExpediaHotelDeeplink,
  supportedCarTrawlerCurrencies,
} from '@fcom/common';
import {
  OrderActions,
  orderData,
  PassengerNameRecord,
  SeatMapActions,
  seatMapForFlightStatus,
  ServicesActions,
} from '@fcom/common-booking';
import { CommonBookingAncillaryService } from '@fcom/common-booking/modules/ancillaries/services';
import { getDapiErrorKey, isFlight } from '@fcom/common-booking/utils/common-booking.utils';
import { CustomServiceType } from '@fcom/dapi/interfaces/services.interface';
import { LanguageService } from '@fcom/ui-translate';
import { setNoAvailability } from '@fcom/common-booking/store/actions/seat-map.actions';
import { ExpediaHotelOrigin, ThirdPartyAncillariesProvider } from '@fcom/common/enums';

import { ThirdPartyAncillariesAttributes, ThirdPartyDeepLink } from '../interfaces';
import { MmbError, MmbStage } from '../enums';
import { finShare, snapshot } from '../../rx';
import { CartActions } from '../../booking/store/actions';
import { travelRequirementsRequired } from '../utils/booking.utils';
import { StateActions } from '../store/actions';
import { isServicesDirty } from '../store/selectors/state.selector';

const REMOVE_TIMEZONE = /(Z|\+|-)(\d|:){0,5}$/;
const FAILED_TO_ADD_SERVICES_STRING = 'Failed to add services for order';

@Injectable()
export class ManageBookingService implements OnDestroy {
  private mmbOrderService = inject(MmbOrderService);
  private store$ = inject<Store<AppState>>(Store);
  private gtmService = inject(GtmService);
  private dataCloudService = inject(DataCloudService);
  private sentryLogger = inject(SentryLogger);
  private commonBookingAncillaryService = inject(CommonBookingAncillaryService);
  private languageService = inject(LanguageService);

  private seatMapSubscriptions: Record<string, Subscription> = {};
  private loadOrderSubscription: Subscription;
  private loadOrderSimpleSubscription: Subscription;
  private setServicesSubscription: Subscription;

  ngOnDestroy(): void {
    Object.values(this.seatMapSubscriptions).forEach((subs) => unsubscribe(subs));
    unsubscribe(this.loadOrderSubscription);
    unsubscribe(this.loadOrderSimpleSubscription);
    unsubscribe(this.setServicesSubscription);
  }

  private trackEligibleForDisruptionRebooking(order: FinnairOrder): void {
    order.eligibilities?.disruptionRebooking
      ?.filter(({ isAllowedToUse }) => isAllowedToUse)
      .forEach(() => {
        this.gtmService.trackElement(
          'view-pnr_selfReacc',
          GaContext.MANAGE_BOOKING,
          ElementTypes.BUTTON,
          undefined,
          ElementActions.VIEW,
          `recLoc-${order.otherInformation.analyticsToken}`
        );
      });
  }

  private trackEligibleForAcknowledge(order: FinnairOrder): void {
    order.eligibilities?.acknowledge
      ?.filter(({ isAllowedToUse }) => isAllowedToUse)
      .forEach(() => {
        this.gtmService.trackElement(
          'view-pnr_acknowledge',
          GaContext.MANAGE_BOOKING,
          ElementTypes.BUTTON,
          undefined,
          ElementActions.VIEW,
          `recLoc-${order.otherInformation.analyticsToken}`
        );
      });
  }

  private trackInvoluntaryCancelEligibility(order: FinnairOrder): void {
    order.eligibilities?.involuntaryCancel
      ?.filter(({ isAllowedToUse, id }) => isAllowedToUse && id === CustomServiceType.JOURNEY)
      .forEach(() => {
        this.gtmService.trackElement(
          'view-pnr_involuntaryCancel',
          GaContext.MANAGE_BOOKING,
          ElementTypes.BUTTON,
          undefined,
          ElementActions.VIEW,
          `recLoc-${order.otherInformation.analyticsToken}`
        );
      });
  }

  private trackVoluntaryCancelEligibility(order: FinnairOrder): void {
    order.eligibilities?.voluntaryCancel
      ?.filter(({ isAllowedToUse, id }) => isAllowedToUse && id === CustomServiceType.JOURNEY)
      .forEach(() => {
        this.gtmService.trackElement(
          'view-pnr_voluntaryCancel',
          GaContext.MANAGE_BOOKING,
          ElementTypes.BUTTON,
          undefined,
          ElementActions.VIEW,
          `recLoc-${order.otherInformation.analyticsToken}`
        );
      });
  }

  /**
   * This should be only used when initially doing post get order in MMB or CHECK_IN form
   * @param PNR form data
   * @param requestInitiator who initiated the call
   * @param trackRetrieval should we track call or not
   * @param resetStage should we set stage to VIEW or not
   * @param cancelStream$ hook to cancel the request
   * @param checkIn was the call from check-in
   */
  retrieveOrderFromForm(
    PNR: PassengerNameRecord,
    requestInitiator: RequestInitiator,
    trackRetrieval: boolean,
    resetStage = true,
    cancelStream$: Observable<boolean> = NEVER,
    checkIn = false
  ): Observable<FinnairOrder> {
    this.store$.dispatch(SeatMapActions.reset());
    this.store$.dispatch(CartActions.reset());
    this.store$.dispatch(OrderActions.setPNR({ PNR }));
    this.store$.dispatch(StateActions.setLoading({ loading: true }));
    this.store$.dispatch(StateActions.clearError());
    const titleRegexPattern = /\b(Mr|Mrs|Ms)\b$/i;
    const trimmedFirstName = PNR.firstName ? PNR.firstName.trim().replace(titleRegexPattern, '')?.trim() : undefined;
    const loadOrder$ = PNR.tourOperatorCode
      ? this.mmbOrderService.loadOrderTourOperator(PNR.lastName, PNR.recLoc, PNR.tourOperatorCode)
      : this.mmbOrderService.loadOrder(PNR.lastName?.trim(), PNR.recLoc, requestInitiator, trimmedFirstName);

    unsubscribe(this.loadOrderSubscription);

    const loadOrderSubscriber$ = loadOrder$.pipe(finShare(), takeUntil(cancelStream$));

    this.loadOrderSubscription = loadOrderSubscriber$.subscribe({
      next: (data: FinnairOrder) => {
        if (trackRetrieval) {
          this.trackElement(data, 'success', data.otherInformation.isGroupBooking ? 'group-booking' : '');
          this.trackFrequentFlyers(data);
          if (travelRequirementsRequired(data, true)) {
            this.trackAPIS(data);
          }

          this.trackEligibleForAcknowledge(data);
          this.trackEligibleForDisruptionRebooking(data);
          this.trackInvoluntaryCancelEligibility(data);
          this.trackVoluntaryCancelEligibility(data);

          this.trackDataCloud(data.passengers[0]);
        }

        this.store$.dispatch(OrderActions.resetCategoriesUpdating());

        if (!checkIn) {
          this.store$.dispatch(StateActions.setLoading({ loading: false }));

          this.updateStore(data);
          if (resetStage) {
            this.store$.dispatch(StateActions.setStage({ stage: MmbStage.VIEW }));
          }
        }
      },
      error: (err: unknown) => {
        if (trackRetrieval) {
          this.trackElement(
            undefined,
            'error',
            PNR.firstName && PNR.lastName ? 'group-booking' : '',
            getDapiErrorKey(err)
          );
        }
        this.store$.dispatch(StateActions.setLoading({ loading: false }));
        this.store$.dispatch(OrderActions.clearPNR());
        this.store$.dispatch(
          StateActions.setError({ error: this.getError(PNR, (err as DapiHttpErrorResponse)?.error?.key) })
        );
      },
    });

    return loadOrderSubscriber$;
  }

  getOrderWithIdAndHash(orderId: string, hash: string, resetStage = true): void {
    unsubscribe(this.loadOrderSimpleSubscription);

    this.store$.dispatch(SeatMapActions.reset());
    this.store$.dispatch(CartActions.reset());
    this.store$.dispatch(StateActions.setLoading({ loading: true }));
    this.store$.dispatch(StateActions.clearError());

    this.loadOrderSimpleSubscription = this.mmbOrderService.getOrderByIdAndHash(orderId, hash).subscribe({
      next: (data: FinnairOrder) => {
        this.store$.dispatch(OrderActions.resetCategoriesUpdating());
        this.store$.dispatch(StateActions.setLoading({ loading: false }));
        this.updateStore(data);
        if (resetStage) {
          this.store$.dispatch(StateActions.setStage({ stage: MmbStage.VIEW }));
        }
      },
      error: (err: unknown) => {
        this.store$.dispatch(StateActions.setLoading({ loading: false }));
        this.store$.dispatch(
          StateActions.setError({
            error: this.getError({ recLoc: orderId }, (err as DapiHttpErrorResponse)?.error?.key),
          })
        );
      },
    });
  }

  private getError(PNR: PassengerNameRecord, errorKey: string | undefined): MmbError {
    switch (errorKey) {
      case DapiErrorCode.ORDER_GROUP_FETCH_WITH_INFANT: {
        return MmbError.GROUP_BOOKING_LOGGING_WITH_INFANT;
      }
      case DapiErrorCode.ORDER_CANCELLED: {
        return MmbError.BOOKING_CANCELLED;
      }
      case DapiErrorCode.ORDER_GROUP_ERROR: {
        return PNR.firstName ? MmbError.GROUP_BOOKING_NOT_FOUND : MmbError.GROUP_BOOKING;
      }
      case DapiErrorCode.GROUP_ORDER_NOT_TICKETED: {
        return MmbError.GROUP_BOOKING_NOT_TICKETED;
      }
      case DapiErrorCode.ORDER_NOT_FOUND: {
        return MmbError.BOOKING_NOT_FOUND;
      }
      case DapiErrorCode.UNABLE_TO_DISPLAY_ORDER: {
        return MmbError.UNEXPECTED_ERROR;
      }
      case DapiErrorCode.ORDER_UNEXPECTED_ERROR: {
        return MmbError.UNEXPECTED_ERROR;
      }
      case DapiErrorCode.ORDER_TIMEOUT_ERROR: {
        return MmbError.UNEXPECTED_ERROR;
      }
      case DapiErrorCode.SEVERAL_RECORDS_FOR_TRAVEL_DOCUMENT: {
        return MmbError.SEVERAL_RECORDS_FOR_TRAVEL_DOCUMENT;
      }
      default: {
        return MmbError.UNEXPECTED_ERROR;
      }
    }
  }

  setServices(
    serviceRequests: FinnairServiceRequestItem[],
    categories: Category[],
    observe: PartialObserver<FinnairOrder> = { complete: noop }
  ): boolean {
    if (snapshot(this.store$.pipe(isServicesDirty()))) {
      const order$ = this.store$.pipe(orderData());
      const order = snapshot(order$);
      const { id, hash } = order;

      categories.forEach((category) => {
        this.store$.dispatch(OrderActions.setCategoryUpdating({ orderPartIds: category }));
      });

      unsubscribe(this.setServicesSubscription);
      this.setServicesSubscription = this.mmbOrderService
        .setServices$(id, hash, serviceRequests)
        .pipe(tap(observe))
        .pipe(
          catchError((error: unknown): Observable<FinnairOrder> => {
            this.sentryLogger.error(FAILED_TO_ADD_SERVICES_STRING, {
              error: mapErrorForSentry(error),
              categories,
            });

            categories.forEach((category) => {
              this.store$.dispatch(ServicesActions.resetCategory({ category }));
            });

            const orderLocation = (error as HttpErrorResponse).headers?.get('Location');
            if (orderLocation) {
              return this.mmbOrderService.getOrderByUrl(orderLocation);
            }
            return order$;
          })
        )
        .subscribe({
          next: (nextOrder) => {
            this.store$.dispatch(OrderActions.setOrderData({ order: nextOrder }));
            categories.forEach((category) => {
              this.store$.dispatch(OrderActions.clearCategoryUpdating({ orderPartIds: category }));
            });
          },
          error: (error: unknown) => {
            this.sentryLogger.error(FAILED_TO_ADD_SERVICES_STRING, { error: mapErrorForSentry(error) });
            this.store$.dispatch(OrderActions.setOrderData({ order }));
            categories.forEach((category) => {
              this.store$.dispatch(OrderActions.clearCategoryUpdating({ orderPartIds: category }));
            });
          },
        });
      return true;
    } else {
      return false;
    }
  }

  removeAllServices(serviceRequests: FinnairServiceRequestItem[], id: string, hash: string): Observable<FinnairOrder> {
    return this.resolveUpdateRequest(
      this.mmbOrderService.setServices$(id, hash, serviceRequests),
      FAILED_TO_ADD_SERVICES_STRING
    );
  }

  updateEmergencyContactDetails(serviceRequest: FinnairEmergencyContactUpdateRequest): Observable<FinnairOrder> {
    const { id, hash } = snapshot(this.store$.pipe(orderData()));
    return this.resolveUpdateRequest(
      this.mmbOrderService.updateEmergencyContactDetails$(id, hash, serviceRequest),
      'Failed to update emergency contact details'
    );
  }

  updateApis(serviceRequest: FinnairAdvancePassengerInformationRequest[]): Observable<FinnairOrder> {
    const { id, hash } = snapshot(this.store$.pipe(orderData()));
    return this.resolveUpdateRequest(
      this.mmbOrderService.updateApis(id, hash, serviceRequest),
      'Failed to update apis information'
    );
  }

  reassignExitRowSeats(reassignExitRowSeatsRequest: CheckInReassignExitSeatsRequest): Observable<FinnairOrder> {
    const { id, hash } = snapshot(this.store$.pipe(orderData()));
    return this.resolveUpdateRequest(
      this.mmbOrderService.reassignExitRowSeats(id, hash, reassignExitRowSeatsRequest),
      'Failed to reassign passenger exit row seats'
    );
  }

  updateContactDetails(serviceRequest: FinnairPassengerRequest): Observable<FinnairOrder> {
    const { id, hash } = snapshot(this.store$.pipe(orderData()));
    return this.resolveUpdateRequest(
      this.mmbOrderService.updateContactDetails(id, hash, serviceRequest),
      'Failed to update passenger contact details'
    );
  }

  updateRegulatoryDetails(serviceRequest: FinnairAdvancePassengerInformationRequestV2[]): Observable<FinnairOrder> {
    const { id, hash } = snapshot(this.store$.pipe(orderData()));
    return this.resolveUpdateRequest(
      this.mmbOrderService.updateRegulatoryDetails(id, hash, serviceRequest),
      'Failed to update regulatory details',
      true
    );
  }

  private resolveUpdateRequest(
    updateRequest$: Observable<FinnairOrder | null>,
    errorMsg: string,
    keepSeatsData = false
  ): Observable<FinnairOrder> {
    const stream$ = updateRequest$.pipe(take(1), share());
    stream$
      .pipe(
        catchError((error: unknown): Observable<FinnairOrder> => {
          this.sentryLogger.error(errorMsg, { error: mapErrorForSentry(error) });
          const orderLocation = (error as HttpErrorResponse).headers?.get('Location');
          if (orderLocation) {
            return this.mmbOrderService.getOrderByUrl(orderLocation);
          }
          return this.store$.pipe(orderData());
        })
      )
      .subscribe((data) => {
        this.updateStore(data, keepSeatsData);
      });

    return stream$;
  }

  private updateStore(order: FinnairOrder, keepSeatsData = false): void {
    this.store$.dispatch(OrderActions.setOrderData({ order, keepSeatsData }));

    this.commonBookingAncillaryService.setServiceCatalog(order);
  }

  triggerFetchSeatMap(flight: FinnairItineraryItemFlight, order: FinnairOrder, forceFetch = false): void {
    const hasAvailability = Object.values(
      order.eligibilities?.serviceCatalog?.categories?.find((c) => c.category === Category.SEAT)?.services?.[
        flight.id
      ] ?? {}
    ).some(({ isAllowedToUse }) => isAllowedToUse);

    if (!hasAvailability) {
      this.store$.dispatch(setNoAvailability({ flightId: flight.id }));
      return;
    }

    const currencyCode = order.prices?.included?.total?.total?.totalAmount?.currencyCode ?? 'EUR';
    const flightId = flight.id;
    const status = snapshot(this.store$.pipe(seatMapForFlightStatus(flightId)));

    const doNotFetchStatuses = [SeatMapStatus.OK_NO_AVAILABILITY, SeatMapStatus.PENDING];
    if (
      (!doNotFetchStatuses.includes(status) &&
        (status !== SeatMapStatus.OK || snapshot(this.store$.pipe(isServicesDirty())))) ||
      forceFetch
    ) {
      unsubscribe(this.seatMapSubscriptions[flightId]);
      const departureDateTime = flight.departure.dateTime;
      this.store$.dispatch(SeatMapActions.startLoading({ flightId }));
      this.seatMapSubscriptions[flightId] = this.commonBookingAncillaryService
        .getOrderSeatMapForFlight(flightId, departureDateTime, order.id, order.hash, currencyCode)
        .subscribe({
          next: (seatMap: SeatmapSvgAndControlData) => {
            this.store$.dispatch(SeatMapActions.setSeatMap({ seatMapForFlight: { seatMap, flightId } }));
            unsubscribe(this.seatMapSubscriptions[flightId]);
          },
          // eslint-disable-next-line rxjs-x/no-implicit-any-catch
          error: (error: Error) => {
            this.sentryLogger.error('Error fetching seat map in mmb', {
              error: mapErrorForSentry(error),
            });
            this.store$.dispatch(SeatMapActions.error({ flightId }));
            unsubscribe(this.seatMapSubscriptions[flightId]);
          },
          complete: () => unsubscribe(this.seatMapSubscriptions[flightId]),
        });
    }
  }

  public getThirdPartyDeepLinks(
    order: FinnairOrder,
    arriving: FinnairTravelEndpoint,
    leaving: FinnairTravelEndpoint,
    thirdPartyAncillaries: ThirdPartyAncillariesAttributes[]
  ): ThirdPartyDeepLink[] {
    return thirdPartyAncillaries.map((thirdParty) => this.getThirdPartyLink(order, arriving, leaving, thirdParty));
  }

  private getExpediaHotelDeeplink(
    order: FinnairOrder,
    arriving: FinnairTravelEndpoint,
    leaving: FinnairTravelEndpoint,
    thirdParty: ThirdPartyAncillariesAttributes
  ): ThirdPartyDeepLink {
    return {
      ...thirdParty,
      url: buildExpediaHotelDeeplink(
        order,
        arriving,
        leaving,
        this.languageService.langValue,
        ExpediaHotelOrigin.TRIP_OVERVIEW
      ),
    };
  }

  private getThirdPartyLink(
    order: FinnairOrder,
    arriving: FinnairTravelEndpoint,
    leaving: FinnairTravelEndpoint,
    thirdParty: ThirdPartyAncillariesAttributes
  ): ThirdPartyDeepLink {
    if (thirdParty.partner === ThirdPartyAncillariesProvider.EXPEDIA_HOTEL) {
      return this.getExpediaHotelDeeplink(order, arriving, leaving, thirdParty);
    }
    const dropOff = this.calculateDropOff(arriving, leaving, thirdParty);
    if (
      thirdParty.partner === ThirdPartyAncillariesProvider.CAR_TRAWLER_RENTAL ||
      thirdParty.partner === ThirdPartyAncillariesProvider.CAR_TRAWLER_TRANSFER
    ) {
      const paxCount = order.passengers.length;
      const currency = supportedCarTrawlerCurrencies(order.prices?.included?.total?.total?.totalAmount?.currencyCode);
      const locale = getCarTrawlerLang(thirdParty.partner, this.languageService.langValue);
      const isOneWay = order.bounds.length === 1;
      const arrivalFlightNumber = order.bounds[0].itinerary.filter(isFlight).at(-1)?.flightNumber;

      const newPickup = {
        ...arriving,
        dateTime: getCarTrawlerDateTime(arriving.dateTime, 1),
      };
      const newDropoff = {
        ...dropOff,
        dateTime: getCarTrawlerDateTime(dropOff.dateTime, isOneWay ? 1 : -2),
      };

      return this.buildCarTrawlerDeepLinkInfo(
        thirdParty.clientId,
        locale,
        thirdParty,
        newPickup,
        newDropoff,
        currency,
        paxCount,
        isOneWay,
        arrivalFlightNumber,
        thirdParty.partner === ThirdPartyAncillariesProvider.CAR_TRAWLER_TRANSFER
      );
    }
  }

  private calculateDropOff(
    arriving: FinnairTravelEndpoint,
    leaving: FinnairTravelEndpoint,
    thirdParty: ThirdPartyAncillariesAttributes
  ): FinnairTravelEndpoint {
    return (
      leaving ?? {
        ...arriving,
        dateTime: new TzDate(arriving.dateTime.replace(REMOVE_TIMEZONE, 'Z'))
          .addHours(24 * thirdParty.defaultStay)
          .toISOString(),
      }
    );
  }

  private buildCarTrawlerDeepLinkInfo(
    clientId: string,
    locale: string,
    thirdParty: ThirdPartyAncillariesAttributes,
    pickup: FinnairTravelEndpoint,
    dropoff: FinnairTravelEndpoint,
    currency: string,
    passengers: number,
    isOneWay: boolean,
    flightNumber: string,
    transfer = false,
    context = 'manage-booking'
  ): ThirdPartyDeepLink {
    let params = new HttpParams()
      .set('clientId', clientId)
      .set('passengers', passengers)
      .set('context', context)
      .set('pickupIATACode', pickup.locationCode)
      .set('pickupDateTime', pickup.dateTime)
      .set('flightNumber', flightNumber);

    if (!transfer) {
      params = params.set('returnDateTime', dropoff.dateTime);
      params = params.set('dropoffIATACode', dropoff.locationCode);
    } else if (isOneWay) {
      params = params.set('oneWay', true);
    } else {
      params = params.set('returnDateTime', dropoff.dateTime);
    }

    params = params.set('currency', currency);

    const urlPrefix = transfer ? 'transfers' : 'cars';

    const thirdPartyInfo: ThirdPartyDeepLink = {
      partner: transfer
        ? ThirdPartyAncillariesProvider.CAR_TRAWLER_TRANSFER
        : ThirdPartyAncillariesProvider.CAR_TRAWLER_RENTAL,
      cmsKey: thirdParty.cmsKey,
      url: `https://${urlPrefix}.finnair.com/${locale}/book?${params.toString()}`,
      partnerIcon: thirdParty.partnerIcon,
    };

    return thirdPartyInfo;
  }

  private trackElement(
    order: FinnairOrder | undefined,
    responseType: string,
    bookingType?: string,
    errorKey?: string
  ): void {
    const error = errorKey ? `-${errorKey}` : '';
    const booking = bookingType ? `-${bookingType}` : '';
    this.gtmService.trackElement(
      `retrieve-booking${booking}-${responseType}${error}`,
      GaContext.MANAGE_BOOKING,
      ElementTypes.FORM,
      undefined,
      ElementActions.SUBMIT,
      order && `recLoc-${order.otherInformation.analyticsToken}`
    );
  }

  private trackAPIS(order: FinnairOrder): void {
    this.gtmService.trackElement(
      'apis-update_required',
      GaContext.MANAGE_BOOKING,
      ElementTypes.FORM,
      undefined,
      ElementActions.SUBMIT,
      `recLoc-${order.otherInformation.analyticsToken}`
    );
  }

  private trackFrequentFlyers(data: FinnairOrder) {
    const maxCardsForPax = Math.max(...(data.passengers ?? []).map((pax) => pax.frequentFlyerCards?.length ?? 0));
    const cards = maxCardsForPax > 0 ? (maxCardsForPax > 1 ? 'many' : 'one') : 'none';
    this.gtmService.trackElement(
      `retrieve-booking_frequent-flyer-cards-${cards}`,
      GaContext.MANAGE_BOOKING,
      ElementTypes.FORM,
      `card-count-${maxCardsForPax}`,
      ElementActions.SUBMIT
    );
  }

  private trackDataCloud(passenger: FinnairPassengerItem): void {
    this.dataCloudService.mmbPaxDetails({
      firstName: passenger.firstName,
      lastName: passenger.lastName,
      emailAddress: passenger.email,
      countryCode: passenger.phoneNumbers?.[0]?.countryPrefix,
      phoneNumber: passenger.phoneNumbers?.[0]?.number,
      memberProgram: passenger.frequentFlyerCards?.[0]?.companyCode,
      memberNumber: passenger.frequentFlyerCards?.[0]?.cardNumber,
    });
  }
}
